import { z } from "zod";
// Shared demo-facing contracts intentionally exclude story content from analytics.
z.config({ jitless: true });
const opaqueIdSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/i, "Expected an opaque ULID");
export const demoPathSchema = z.enum(["memoir", "bedtime", "family"]);
export const launchStateSchema = z.enum(["working", "sandbox", "roadmap"]);
export const catalogExperienceSchema = z
    .object({
    experiencePillar: z.string().trim().min(1).max(80),
    launchState: launchStateSchema,
    capabilities: z.array(z.string().trim().min(1).max(120)).min(1).max(12),
    physicalSpecs: z.array(z.string().trim().min(1).max(120)).max(12),
    includedLimits: z.array(z.string().trim().min(1).max(120)).max(12),
    sampleAssets: z.array(z.string().trim().min(1).max(500)).max(12),
    estimatedDeliveryWindow: z.string().trim().min(1).max(120),
    demoPath: demoPathSchema,
})
    .strict();
export const demoCatalogExperiences = [
    {
        experiencePillar: "Preserve a Life",
        launchState: "working",
        capabilities: [
            "Text and existing voice capture",
            "Source-linked revisions",
            "Explicit approval",
        ],
        physicalSpecs: ["6 × 9 in hardcover", "Black-and-white or color interior"],
        includedLimits: ["One 20-chapter book project", "Family review workspace"],
        sampleAssets: ["/samples/memoir-preview"],
        estimatedDeliveryWindow: "Sandbox estimate: 10–14 days after print approval",
        demoPath: "memoir",
    },
    {
        experiencePillar: "Turn a Memory into Bedtime",
        launchState: "working",
        capabilities: ["Four-step story studio", "Narration", "Print-ready keepsake preview"],
        physicalSpecs: ["8.5 × 8.5 in color hardcover", "QR-linked private narration"],
        includedLimits: ["One illustrated story", "Generation budget capped at $12"],
        sampleAssets: ["/samples/bedtime-preview"],
        estimatedDeliveryWindow: "Sandbox estimate: 10–14 days after print approval",
        demoPath: "bedtime",
    },
    {
        experiencePillar: "Build Together",
        launchState: "working",
        capabilities: [
            "Family invitations",
            "Owner, editor, and contributor roles",
            "Contribution status",
        ],
        physicalSpecs: ["6 × 9 in color hardcover", "Custom cover and chapter order"],
        includedLimits: ["Up to 10 invited contributors", "One family book project"],
        sampleAssets: ["/samples/family-preview"],
        estimatedDeliveryWindow: "Sandbox estimate: 10–14 days after print approval",
        demoPath: "family",
    },
];
for (const experience of demoCatalogExperiences)
    catalogExperienceSchema.parse(experience);
export const chapterPresentationSchema = z
    .object({
    chapterId: opaqueIdSchema,
    displayTitle: z.string().trim().min(1).max(200),
    caption: z.string().trim().max(500),
    featuredAssetId: opaqueIdSchema.optional(),
})
    .strict();
export const bookPresentationSchema = z
    .object({
    coverTitle: z.string().trim().min(1).max(160),
    subtitle: z.string().trim().max(240),
    byline: z.string().trim().min(1).max(120),
    coverAssetId: opaqueIdSchema.optional(),
    orderedChapterIds: z.array(opaqueIdSchema).min(1).max(200),
    chapters: z.array(chapterPresentationSchema).min(1).max(200),
})
    .strict()
    .superRefine((presentation, context) => {
    if (new Set(presentation.orderedChapterIds).size !== presentation.orderedChapterIds.length)
        context.addIssue({
            code: "custom",
            path: ["orderedChapterIds"],
            message: "Chapter order must not contain duplicates.",
        });
    const presentationIds = presentation.chapters.map((chapter) => chapter.chapterId);
    if (presentationIds.length !== presentation.orderedChapterIds.length ||
        presentationIds.some((id) => !presentation.orderedChapterIds.includes(id)))
        context.addIssue({
            code: "custom",
            path: ["chapters"],
            message: "Presentation chapters must exactly match the ordered chapter IDs.",
        });
});
export const generateFollowUpsCommandSchema = z
    .object({
    provenanceRevisionId: opaqueIdSchema,
})
    .strict();
export const followUpQuestionSchema = z
    .object({
    id: z.string().regex(/^follow-up-[1-3]$/),
    question: z.string().trim().min(8).max(300),
    sourceFactIndex: z.number().int().nonnegative(),
})
    .strict();
export const followUpsResponseSchema = z
    .object({
    data: z
        .object({
        questions: z.array(followUpQuestionSchema).min(2).max(3),
        provenanceRevisionId: opaqueIdSchema,
        deterministicFallbackUsed: z.boolean(),
    })
        .strict(),
})
    .strict();
export const demoOrderStateSchema = z.enum([
    "checkout_pending",
    "paid_test",
    "rendering",
    "preflight_passed",
    "submitted_sandbox",
    "accepted_sandbox",
    "failed",
]);
export const analyticsEventNameSchema = z.enum([
    "path_selected",
    "answer_started",
    "source_uploaded",
    "draft_generated",
    "revision_approved",
    "preview_opened",
    "invite_sent",
    "checkout_started",
    "checkout_completed",
    "print_submission",
    "buyer_rehearsal_started",
    "memory_capture_started",
    "transcript_confirmed",
    "accepted_book_revealed",
    "buyer_rehearsal_completed",
]);
const telemetryCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9_.:-]*$/i, "Expected a content-free telemetry code");
export const contentFreeAnalyticsEventSchema = z
    .object({
    eventId: opaqueIdSchema,
    name: analyticsEventNameSchema,
    occurredAt: z.string().datetime(),
    path: demoPathSchema.optional(),
    bookId: opaqueIdSchema.optional(),
    chapterId: opaqueIdSchema.optional(),
    orderId: opaqueIdSchema.optional(),
    durationMs: z.number().int().nonnegative().max(86_400_000).optional(),
    status: telemetryCodeSchema.optional(),
    errorCode: telemetryCodeSchema.optional(),
    providerMode: z.enum(["test", "sandbox", "unavailable", "deterministic"]).optional(),
})
    .strict();
export const demoRoutes = {
    hub: "/demo",
    memoir: "/demo/memoir",
    bedtime: "/demo/bedtime",
    family: "/demo/family",
    diagnostics: "/app/demo/diagnostics",
};
export function chapterFollowUpsPath(bookId, chapterId) {
    return `/books/${bookId}/chapters/${chapterId}/follow-ups`;
}
//# sourceMappingURL=demo.js.map